/**
 * The shared answer component for the ASK AI CSV download feature.
 *
 * Both chat surfaces render an assistant answer by injecting already-parsed
 * markup, so there is no React node for a table to hang a sibling control on.
 * This component closes that gap: it owns the host element that receives the
 * answer HTML, so it can detect tables in the same commit that injects the
 * markup, and it attaches a control to each detected table by inserting an
 * empty mount element next to the table and portalling a React control into it.
 *
 * Three properties of that arrangement carry weight:
 *
 *   - **The effect key is the `html` string, not the message identity.** Every
 *     path that produces answer HTML runs the same effect — a fresh answer, a
 *     re-render, and an answer restored from conversation history — because a
 *     restored answer produces a new `html` string. Req 1.6 therefore needs no
 *     separate code path, and adding one would only give the two paths a way to
 *     disagree.
 *   - **Cleanup removes every mount node the effect created and clears the
 *     target state.** A changed answer leaves no orphaned control, never
 *     double-inserts, and never portals a control into a node that the new
 *     `innerHTML` has already detached.
 *   - **Nothing answer-derived is concatenated into markup.** The mount element
 *     comes from `document.createElement`, is placed with
 *     `insertAdjacentElement`, and carries a static class name. The one
 *     existing `dangerouslySetInnerHTML` sink is unchanged and no second one is
 *     added, so this feature does not widen the raw-markup injection surface it
 *     inherits. Cell text is read only through `textContent`, one layer down in
 *     `tableToCellTextGrid`.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Alert, Snackbar } from "@mui/material";

import ConfirmationDialog from "../utils/ConfirmationDialog";
import DownloadControl from "./DownloadControl";
import DownloadOffer from "./DownloadOffer";
import { detectAnswerTables } from "./detectAnswerTables";
import { DownloadDeps } from "./downloadCsvFile";
import {
  largeTableConfirmMessage,
  useTableCsvDownload,
} from "./useTableCsvDownload";
import { AnswerTableTarget } from "./types";

// ── Constants ──────────────────────────────────────────────────────────────

/**
 * Class name of the inserted mount element. A literal, so no answer content
 * reaches an attribute value, and stable, so a test or a stylesheet can find
 * the controls of an answer.
 */
export const MOUNT_CLASS_NAME = "finn-csv-download-mount";

/**
 * How long an error message stays up before dismissing itself, matching the
 * snackbar pattern already used in this package (`ShareLinkDialog`). The
 * message is also dismissible at any point (Req 7.1, 7.2).
 */
export const ERROR_SNACKBAR_AUTO_HIDE_MS = 6000;

/**
 * The no-tables target list, shared and frozen. Reusing one identity lets
 * React bail out of a re-render when an answer with no table is detected and
 * when cleanup clears an already-empty list.
 */
const EMPTY_TARGETS: readonly AnswerTableTarget[] = Object.freeze([]);

// ── Props ──────────────────────────────────────────────────────────────────

export interface AnswerTableDownloadsProps {
  /** Already-parsed answer HTML, as each surface produces it today. */
  html: string;
  /** Zero-based position of this answer in the conversation (Req 4.1). */
  messageIndex: number;
  /** Conversation identifier, absent while the conversation is unsaved. */
  conversationId?: string | null;
  /** Class name for the host element, e.g. `wbr-md`. */
  className?: string;
  /**
   * Receives the host element, so a surface that already runs its own
   * post-render pass over the injected DOM — the KaTeX typesetting in
   * `WBRChatbox` — keeps operating on the same element.
   */
  hostRef?: React.MutableRefObject<HTMLElement | null>;
  /**
   * Selector of the wrapper a surface puts around each table, e.g.
   * `.wbr-table-scroll`. When given, the control is inserted after the wrapper
   * rather than after the table, so it sits under a horizontal-scroll box
   * instead of inside it (Req 2.2).
   */
  tableWrapperSelector?: string;
  /**
   * Clock read once per download. Test seam; production callers omit it and
   * get the system clock.
   */
  now?: () => Date;
  /**
   * Download seam. Test seam; production callers omit it and get the real
   * `URL` and `document`.
   */
  downloadDeps?: DownloadDeps;
}

// ── Component ──────────────────────────────────────────────────────────────

/**
 * An assistant answer, plus one download control per table it contains.
 *
 * ```text
 * render(html)  ->  host.innerHTML = html
 *               ->  commit
 *               ->  useEffect([html]): detectAnswerTables(host)
 *                                      insert one mount div per table
 *                                      setTargets(...)
 *               ->  createPortal(DownloadControl, mountNode) per target
 * ```
 *
 * The effect depends on `html` and on `tableWrapperSelector` only, never on the
 * targets it produces, so setting the target state cannot make it run again.
 */
const AnswerTableDownloads: React.FC<AnswerTableDownloadsProps> = ({
  html,
  messageIndex,
  conversationId,
  className,
  hostRef,
  tableWrapperSelector,
  now,
  downloadDeps,
}) => {
  const hostElementRef = useRef<HTMLDivElement | null>(null);
  const [targets, setTargets] =
    useState<readonly AnswerTableTarget[]>(EMPTY_TARGETS);

  const {
    phase,
    busyTableIndex,
    confirmRowCount,
    error,
    requestDownload,
    confirmDownload,
    cancelDownload,
    dismissError,
  } = useTableCsvDownload({
    messageIndex,
    conversationId,
    now,
    downloadDeps,
  });

  /**
   * Keep the host element in this component's own ref and in the forwarded one.
   *
   * A callback ref rather than a shared ref object, because both holders need
   * the element: the detection effect reads `hostElementRef`, and the surface's
   * own effect reads the ref it passed in. React also calls this with `null` on
   * unmount, which clears the forwarded ref rather than leaving the surface
   * holding a detached element.
   */
  const assignHost = useCallback(
    (node: HTMLDivElement | null): void => {
      hostElementRef.current = node;
      if (hostRef !== undefined) {
        hostRef.current = node;
      }
    },
    [hostRef]
  );

  /**
   * Detect the tables of this answer and give each one a mount element
   * (Req 2.1, 5.1).
   *
   * The anchor is the table's wrapper when the surface wraps its tables and
   * the table itself otherwise, so the control lands after the wrapper rather
   * than inside it (Req 2.2). An answer with no table produces no target,
   * hence no control and no offer (Req 1.5).
   */
  useEffect(() => {
    const host = hostElementRef.current;
    if (host === null) {
      return undefined;
    }

    const created: AnswerTableTarget[] = detectAnswerTables(host).map(
      (table, tableIndex) => {
        const mountNode = document.createElement("div");
        mountNode.className = MOUNT_CLASS_NAME;
        const anchor =
          (tableWrapperSelector === undefined
            ? null
            : table.closest(tableWrapperSelector)) ?? table;
        anchor.insertAdjacentElement("afterend", mountNode);
        return { tableIndex, table, mountNode };
      }
    );
    setTargets(created.length === 0 ? EMPTY_TARGETS : created);

    return () => {
      // Every node this run inserted goes, so a changed answer cannot leave an
      // orphaned control behind and cannot end up with two controls on one
      // table. `remove()` is a no-op for a node the new `innerHTML` already
      // detached.
      created.forEach(({ mountNode }) => mountNode.remove());
      // Dropping the targets in the same flush is what keeps a stale target
      // from being portalled into a detached mount node.
      setTargets(EMPTY_TARGETS);
    };
  }, [html, tableWrapperSelector]);

  /** Start the download for the table a control belongs to (Req 5.2). */
  const handleActivate = useCallback(
    (tableIndex: number): void => {
      const target = targets.find(
        (candidate) => candidate.tableIndex === tableIndex
      );
      if (target === undefined) {
        return;
      }
      requestDownload({ table: target.table, tableIndex: target.tableIndex });
    },
    [targets, requestDownload]
  );

  return (
    <>
      {/*
        The one injection sink, inherited from both surfaces and unchanged.
        Everything this component adds is a React element or a DOM node built
        with `createElement`, so no answer-derived string is concatenated into
        markup.
      */}
      <div
        className={className}
        ref={assignHost}
        dangerouslySetInnerHTML={{ __html: html }}
      />

      {/* One offer per answer that has at least one table (Req 2.4). */}
      {targets.length > 0 ? <DownloadOffer /> : null}

      {/* One control per table, inside the answer, next to its table. */}
      {targets.map((target) =>
        createPortal(
          <DownloadControl
            tableIndex={target.tableIndex}
            busy={busyTableIndex === target.tableIndex}
            onActivate={handleActivate}
          />,
          target.mountNode,
          String(target.tableIndex)
        )
      )}

      {/* Large-table prompt (Req 6.1, 6.2, 6.3). */}
      <ConfirmationDialog
        isOpen={phase === "confirming"}
        onClose={cancelDownload}
        onConfirm={confirmDownload}
        message={
          confirmRowCount === null
            ? ""
            : largeTableConfirmMessage(confirmRowCount)
        }
      />

      {/*
        Both error paths (Req 7.1, 7.2). Neither one touches the answer, so it
        stays displayed while the message is up.
      */}
      <Snackbar
        open={error !== null}
        autoHideDuration={ERROR_SNACKBAR_AUTO_HIDE_MS}
        onClose={dismissError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={dismissError} severity="error" sx={{ width: "100%" }}>
          {error ?? ""}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AnswerTableDownloads;
